// src/firebase.js
import {initializeApp, getApps} from "firebase/app";
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from "firebase/firestore";
import {connectStorageEmulator, getStorage} from "firebase/storage";
import {connectFunctionsEmulator, getFunctions} from "firebase/functions";
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  initializeAuth,
  onAuthStateChanged,
} from "firebase/auth";
import {connectFirestoreEmulator} from "firebase/firestore";
import {initAppCheck, ensureAppCheck} from "./appheck"; // ✅ fixed path
import {restoreBrowserAuthSession} from "./authSessionLifecycle";
import {initializeFirebaseAnalyticsTransport} from "./firebaseAnalyticsTransport";
import {recordAnalyticsInspection, sanitizeAnalyticsParams} from "./analyticsInspection";
// Keep this import on the Webpack-processed `.js` entry. Create React App treats
// `.cjs` files under `src/` as static assets, which makes their exports unusable
// in the browser bundle.
import * as runtimeConfig from "./runtimeConfig.js";

const runtime = runtimeConfig.buildRuntimeConfig(process.env);
const {firebaseConfig} = runtime;

// ---------- Single app (true singleton across HMR/multi-import) ----------
function assertFirebaseAppMatchesRuntime(existingApp, expectedConfig) {
  const actual = existingApp?.options || {};
  if (
    actual.projectId !== expectedConfig.projectId ||
    actual.authDomain !== expectedConfig.authDomain
  ) {
    throw new Error(
      "The active Firebase app does not match this deployment's project or Auth domain.",
    );
  }
}

const defaultApp = getApps().find((candidate) => candidate.name === "[DEFAULT]");
if (defaultApp) assertFirebaseAppMatchesRuntime(defaultApp, firebaseConfig);
const app = defaultApp || initializeApp(firebaseConfig);

// Register App Check before Firestore or any other protected Firebase service
// can make its first request. Enterprise token exchange is asynchronous, so the
// application bootstrap waits for this promise before mounting Firestore users.
const recaptchaSiteKey = runtime.recaptchaSiteKey;
let appCheckReady = Promise.resolve(null);
if (typeof window !== "undefined") {
  initAppCheck(recaptchaSiteKey, {
    providerName: runtime.appCheckProvider,
    debugToken: runtime.appCheckDebugToken,
    prefetch: true,
  });
  appCheckReady = ensureAppCheck(6500);
}

// ---------- Firestore ----------
// ---------- Firestore ----------
const isSafari =
  typeof navigator !== "undefined" &&
  /Safari/i.test(navigator.userAgent) &&
  !/Chrome/i.test(navigator.userAgent) &&
  !/CriOS/i.test(navigator.userAgent);

const FS_OPTIONS = {
  experimentalForceLongPolling: false,
  experimentalAutoDetectLongPolling: true,
  useFetchStreams: false,
  timeoutSeconds: 30,

  // IMPORTANT: disable IndexedDB persistence on Safari to prevent stale cached data
  ...(isSafari
    ? {}
    : { localCache: persistentLocalCache({tabManager: persistentMultipleTabManager()}) }),
};

let db;
try {
  db = initializeFirestore(app, FS_OPTIONS);
} catch {
  db = getFirestore(app);
}

// ---------- Storage / Functions / Auth ----------
const storage = getStorage(app);
// Public catalog consumers share the already initialized Firestore instance.
// A second Lite client can race App Check during a cold boot and leave its
// first request pending even though the primary client is ready.
const catalogDb = db;

// ✅ Force region so every callable uses the same endpoint
const functions = getFunctions(app, runtime.region);

// ✅ Auth MUST come from the same `app` instance (prevents “signed in but 401” issues)
let auth;
try {
  // Configure persistence as part of Auth construction. Calling getAuth()
  // first and migrating persistence afterward can leave Safari's initial auth
  // observer pending even when a valid local session already exists.
  auth = initializeAuth(app, {persistence: browserLocalPersistence});
} catch {
  // HMR or another early import may already have initialized the singleton.
  auth = getAuth(app);
}

if (runtime.useEmulators && typeof window !== "undefined") {
  const marker = "__vtkFirebaseEmulatorsConnected";
  if (!window[marker]) {
    connectFirestoreEmulator(db, runtime.emulatorHost, runtime.emulatorPorts.firestore);
    connectFunctionsEmulator(functions, runtime.emulatorHost, runtime.emulatorPorts.functions);
    connectAuthEmulator(
      auth,
      `http://${runtime.emulatorHost}:${runtime.emulatorPorts.auth}`,
      {disableWarnings: true},
    );
    connectStorageEmulator(storage, runtime.emulatorHost, runtime.emulatorPorts.storage);
    window[marker] = true;
  }
}

// Persistence is installed atomically by initializeAuth above. Keep the
// exported promise for existing bootstrap consumers without starting a second
// persistence migration.
const authPersistenceReady = Promise.resolve();
const authStateReady = typeof window === "undefined" ? Promise.resolve(null) :
  restoreBrowserAuthSession({
    authInstance: auth,
    // Persistence is already part of the Auth instance.
    setPersistence: async () => authPersistenceReady,
    persistence: browserLocalPersistence,
    subscribe: onAuthStateChanged,
  });

// ---------- Analytics ----------
let analytics = null;
if (typeof window !== "undefined" && firebaseConfig.measurementId && !runtime.useEmulators) {
  analytics = initializeFirebaseAnalyticsTransport(
    firebaseConfig.measurementId,
    window,
    document,
  );
}

function logFirebaseEvent(eventName, params = {}) {
  const safeParams = sanitizeAnalyticsParams({
    ...params,
    ...(runtime.deploymentEnvironment === "staging" ? {environment: "staging"} : {}),
  });
  recordAnalyticsInspection(eventName, safeParams);
  if (analytics) {
    try {
      analytics.logEvent(eventName, safeParams);
      return true;
    } catch {}
  }
  return false;
}

function getFirebaseAnalyticsState() {
  return Object.freeze({
    initialized: Boolean(analytics),
    measurementId: firebaseConfig.measurementId || null,
    transport: "firebase-analytics-gtag",
  });
}

export {
  app,
  appCheckReady,
  auth,
  authPersistenceReady,
  authStateReady,
  catalogDb,
  db,
  ensureAppCheck,
  firebaseConfig,
  functions,
  getFirebaseAnalyticsState,
  logFirebaseEvent,
  storage,
};
export default app;
